/**
 * HospiceOSPage Content Component
 * 
 * Refactored page content that consumes state from HOSContext.
 * This component focuses on rendering while state management is handled by the context.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useHOS, EVENT_CONFIG } from "@/contexts/HOSContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { useRealtimeDashboard } from "@/hooks/useRealtimeDashboard";
import {
  Search, FileText, Pill, Activity, Heart, Users,
  Calendar, AlertTriangle, Clock, User, ChevronRight, Loader2,
  Stethoscope, Package, Sparkles, Bell, Zap, Eye, Keyboard, Brain,
  AlertCircle, Filter, ChevronDown, Shield, Lightbulb, TrendingUp, Sun, Moon,
  Wifi, WifiOff, ArrowRight
} from "lucide-react";

// Components
import SmartDocumentation from "@/components/ehr/SmartDocumentation";
import IDGVoiceDictation from "@/components/ehr/IDGVoiceDictation";
import VoiceCommandSettings from "@/components/VoiceCommandSettings";
import ProactiveAIChat from "@/components/ProactiveAIChat";
import { HOPEForm, HOPERecordsList, IQIESExport } from "@/components/HOPEForms";
import { HOPETemplateSelector, IQIESScheduleManager, QuickTemplateButton } from "@/components/HOPETemplates";
import { HOSBreadcrumbs } from "@/utils/hosMigration";
import { ClinicalLogicDashboard } from "@/components/ClinicalLogicDashboard";
import { AutomatedSynthesisSettings } from "@/components/AutomatedSynthesisSettings";
import { HospiceVisitTemplateBuilder } from "@/components/HospiceVisitTemplateBuilder";
import { ComplianceDashboard } from "@/components/ComplianceDashboard";
import OfflineStatusIndicator from "@/components/OfflineStatusIndicator";
import PatientJourneyDashboard from "@/components/PatientJourneyDashboard";
import ClinicalAIAgentPanel from "@/components/ClinicalAIAgentPanel";
import { ComplianceSummaryWidget } from "@/components/ComplianceSummaryWidget";
import { VolunteerSummaryWidget } from "@/components/VolunteerSummaryWidget";
import { FivePercentComplianceWidget } from "@/components/FivePercentComplianceWidget";

// Overlay components
import { PatientQuickSwitch } from "@/components/overlays";
import { CommandPalette } from "@/components/CommandPalette";
import { OverlayRenderer } from "@/components/OverlayRenderer";
import { PatientContextCard } from "@/components/PatientContextCard";
import { AISuggestionsPanel } from "@/components/AISuggestionsPanel";
import ExportReportOverlay from "@/components/ExportReportOverlay";
import LCDEligibilityWidget from "@/components/LCDEligibilityWidget";
import EligibilityAlertsPanel from "@/components/EligibilityAlertsPanel";
import CensusEligibilityWidget from "@/components/CensusEligibilityWidget";
import PredictiveAnalyticsDashboard from "@/components/PredictiveAnalyticsDashboard";
import UnifiedVisitNote, { UnifiedVisitTile } from "@/components/ehr/UnifiedVisitNote";
import VisitSyncAdmin from "@/components/ehr/VisitSyncAdmin";

// New overlay imports for command palette
import LCDEligibilityOverlay from "@/components/overlays/LCDEligibilityOverlay";
import AICarePlanOverlay from "@/components/overlays/AICarePlanOverlay";
import RCMManagementOverlay from "@/components/overlays/RCMManagementOverlay";
import StaffSchedulingOverlay from "@/components/overlays/StaffSchedulingOverlay";
import AcuityTrendsOverlay from "@/components/overlays/AcuityTrendsOverlay";
import FallEventOverlay from "@/components/overlays/FallEventOverlay";
import FallRiskDashboardOverlay from "@/components/overlays/FallRiskDashboardOverlay";
import IDGVoiceMeetingOverlay from "@/components/overlays/IDGVoiceMeetingOverlay";
import IDGMeetingAnalyticsOverlay from "@/components/overlays/IDGMeetingAnalyticsOverlay";
import IDGInsightsEmailOverlay from "@/components/overlays/IDGInsightsEmailOverlay";
import VoiceHelpOverlay from "@/components/overlays/VoiceHelpOverlay";
import ClinicalAssessmentsPanel from "@/components/ClinicalAssessmentsPanel";

// Icon mapping
const ICONS = {
  "file-text": FileText,
  "pill": Pill,
  "users": Users,
  "heart-pulse": Heart,
  "clipboard-list": FileText,
  "bandage": FileText,
  "target": FileText,
  "activity": Activity,
  "message-circle": FileText,
  "heart": Heart,
  "stethoscope": Stethoscope,
  "package": Package,
  "calendar": Calendar,
  "phone": FileText,
  "alert-triangle": AlertTriangle,
  "brain": Brain,
  "shield": Shield
};

export default function HospiceOSPageContent() {
  const {
    // Patient state
    selectedPatient,
    patientContext,
    patientList,
    timeline,
    loading,
    
    // Overlay state
    activeOverlay,
    overlayData,
    openOverlay,
    closeOverlay,
    
    // Command palette
    commandOpen,
    setCommandOpen,
    allCommands,
    contextualSuggestions,
    executeCommand,
    searchCommands,
    
    // Patient quick-switch
    quickSwitchOpen,
    setQuickSwitchOpen,
    switchPatient,
    
    // Voice
    isRecording,
    transcription,
    setTranscription,
    
    // Timeline
    timelineFilters,
    setTimelineFilters,
    fetchTimeline,
    
    // Compliance
    complianceResult,
    noteContent,
    checkCompliance,
    
    // Utils
    fetchPatientContext
  } = useHOS();

  // Get theme context
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  // Get auth for user ID
  const { user } = useAuth();

  // Real-time WebSocket connection for dashboard updates
  const handleRealtimeUpdate = useCallback((type, payload) => {
    switch (type) {
      case 'alert':
        if (payload?.priority === 'critical') {
          toast.error(payload.message || 'Critical Alert', {
            description: payload.patient_name,
            duration: 10000
          });
        }
        break;
      case 'stats':
      case 'compliance':
      case 'visit':
        // Could trigger a context refresh here if needed
        break;
      default:
        break;
    }
  }, []);

  const { connected: wsConnected, lastUpdate } = useRealtimeDashboard(
    user?.id || user?.user_id,
    handleRealtimeUpdate
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K or Ctrl+K to open command palette
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCommandOpen(true);
      }
      // Escape to close
      if (e.key === "Escape") {
        setCommandOpen(false);
        closeOverlay();
      }
      // Cmd+N for new note
      if ((e.metaKey || e.ctrlKey) && e.key === "n" && !commandOpen) {
        e.preventDefault();
        openOverlay("visit_note");
      }
      // Cmd+M for MAR
      if ((e.metaKey || e.ctrlKey) && e.key === "m" && !commandOpen) {
        e.preventDefault();
        openOverlay("mar");
      }
      // Cmd+J for Patient Journey
      if ((e.metaKey || e.ctrlKey) && e.key === "j" && !commandOpen) {
        e.preventDefault();
        openOverlay("patient_journey");
      }
      // Ctrl+A for Clinical AI Agent (using Ctrl+Shift+A to avoid select all conflict)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === "a" && !commandOpen) {
        e.preventDefault();
        openOverlay("clinical_ai_agent");
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [commandOpen, openOverlay, closeOverlay, setCommandOpen]);

  // Show filters state
  const [showFilters, setShowFilters] = useState(false);

  // Get icon component
  const getIcon = (iconName) => {
    return ICONS[iconName] || FileText;
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      isDark ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'
    }`} data-testid="hospice-os">
      {/* Top Bar */}
      <div className={`sticky top-0 z-40 backdrop-blur border-b transition-colors ${
        isDark 
          ? 'bg-slate-900/95 border-slate-800' 
          : 'bg-white/95 border-gray-200 shadow-sm'
      }`}>
        {/* Breadcrumbs */}
        <div className="px-6 pt-2">
          <HOSBreadcrumbs />
        </div>
        
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg">HOS</span>
            </div>
            
            {/* Command Palette Trigger */}
            <Button
              variant="outline"
              className={`hidden sm:flex w-40 md:w-64 justify-start transition-colors ${
                isDark 
                  ? 'bg-slate-800/50 border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800' 
                  : 'bg-white border-gray-300 text-gray-500 hover:text-gray-900 hover:bg-gray-50'
              }`}
              onClick={() => setCommandOpen(true)}
              data-testid="command-palette-trigger"
            >
              <Search className="w-4 h-4 mr-2" />
              <span className="hidden md:inline">Search commands...</span>
              <span className="md:hidden">Search</span>
              <kbd className={`ml-auto text-xs px-1.5 py-0.5 rounded hidden md:inline ${
                isDark ? 'bg-slate-700' : 'bg-gray-200 text-gray-600'
              }`}>⌘K</kbd>
            </Button>
          </div>
          
          {/* Right Side Controls */}
          <div className="flex items-center gap-4">
            {/* Real-time Connection Indicator */}
            <div 
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
                wsConnected 
                  ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
                  : isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'
              }`}
              title={wsConnected ? `Live updates - Last: ${lastUpdate ? new Date(lastUpdate).toLocaleTimeString() : 'Just connected'}` : 'Connecting to real-time updates...'}
              data-testid="realtime-indicator"
            >
              {wsConnected ? (
                <>
                  <Wifi className="w-3 h-3" />
                  <span>Live</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-3 h-3 animate-pulse" />
                  <span>Connecting</span>
                </>
              )}
            </div>

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            
            {/* Patient Selector */}
            {selectedPatient && (
              <>
                <OfflineStatusIndicator compact={true} />
                
                <div 
                  className={`flex items-center gap-3 rounded-lg px-4 py-2 cursor-pointer transition-colors ${
                    isDark 
                      ? 'bg-slate-800 hover:bg-slate-700' 
                      : 'bg-white border border-gray-200 hover:bg-gray-50 shadow-sm'
                  }`}
                  onClick={() => setQuickSwitchOpen(true)}
                  title="Click to switch patients"
                  data-testid="patient-selector"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isDark ? 'bg-teal-500/20' : 'bg-teal-100'
                  }`}>
                    <User className="w-4 h-4 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{selectedPatient.name}</p>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-xs ${
                        isDark 
                          ? 'border-teal-500/50 text-teal-400' 
                          : 'border-teal-500 text-teal-600 bg-teal-50'
                      }`}>
                        {patientContext?.quick_stats?.loc || "RHC"}
                      </Badge>
                      <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        Day {patientContext?.quick_stats?.days_in_program || 0}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                </div>
                
                <Button variant="ghost" size="icon" className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                  <Bell className="w-5 h-5" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Patient Quick-Switch Sidebar */}
      <PatientQuickSwitch
        patients={patientList.length > 0 ? patientList : [
          { id: "demo-1", name: "John Smith", mrn: "MRN-001", status: "active" },
          { id: "demo-2", name: "Mary Johnson", mrn: "MRN-002", status: "active" },
          { id: "demo-3", name: "Robert Williams", mrn: "MRN-003", status: "active" },
        ]}
        selectedPatient={selectedPatient}
        onSelectPatient={switchPatient}
        isOpen={quickSwitchOpen}
        onClose={() => setQuickSwitchOpen(false)}
      />

      {/* Main Content */}
      <div className="flex overflow-x-hidden">
        {/* Sidebar - Quick Actions & Alerts - Hidden on mobile */}
        <div className={`hidden lg:block w-72 border-r min-h-[calc(100vh-60px)] p-4 transition-colors overflow-y-auto shrink-0 ${
          isDark 
            ? 'bg-slate-900 border-slate-800' 
            : 'bg-white border-gray-200'
        }`}>
          {/* Compliance Summary Widget - Compact at top */}
          <div className={`mb-4 pb-4 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
            <ComplianceSummaryWidget isDark={isDark} compact={true} />
          </div>
          
          {/* Volunteer Management Widget */}
          <div className={`mb-4 pb-4 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
            <VolunteerSummaryWidget isDark={isDark} compact={true} />
          </div>
          
          {/* 5% Compliance Widget */}
          <div className={`mb-4 pb-4 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
            <FivePercentComplianceWidget isDark={isDark} compact={true} />
          </div>
          
          {/* LCD Eligibility Widget - Shows when patient is selected */}
          {selectedPatient && (
            <div className={`mb-4 pb-4 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
              <LCDEligibilityWidget 
                patient={selectedPatient}
                isDark={isDark}
                compact={true}
              />
            </div>
          )}
          
          {/* Eligibility Alerts Panel - Shows active alerts */}
          <div className={`mb-4 pb-4 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
            <EligibilityAlertsPanel 
              isDark={isDark}
              compact={true}
              maxAlerts={5}
            />
          </div>
          
          {/* Predictive Analytics Widget */}
          <div className={`mb-4 pb-4 border-b ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
            <PredictiveAnalyticsDashboard 
              isDark={isDark}
              compact={true}
            />
          </div>
          
          {/* AI Suggestions Panel */}
          <AISuggestionsPanel 
            patientContext={patientContext}
            onActionSelect={(action) => openOverlay(action)}
            isDark={isDark}
          />
          
          {/* Alerts */}
          {patientContext?.alerts?.length > 0 && (
            <div className="mb-6">
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${
                isDark ? 'text-slate-500' : 'text-gray-500'
              }`}>Alerts</h3>
              <div className="space-y-2">
                {patientContext.alerts.map((alert, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg border ${
                      alert.severity === "high" 
                        ? isDark ? "bg-red-500/10 border-red-500/30" : "bg-red-50 border-red-200" 
                        : alert.severity === "warning" 
                          ? isDark ? "bg-amber-500/10 border-amber-500/30" : "bg-amber-50 border-amber-200"
                          : isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle className={`w-4 h-4 mt-0.5 ${
                        alert.severity === "high" ? "text-red-500" :
                        alert.severity === "warning" ? "text-amber-500" :
                        isDark ? "text-slate-400" : "text-gray-400"
                      }`} />
                      <p className={`text-sm ${isDark ? '' : 'text-gray-700'}`}>{alert.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Quick Actions */}
          <div className="mb-6">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${
              isDark ? 'text-slate-500' : 'text-gray-500'
            }`}>Quick Actions</h3>
            <div className="space-y-1">
              {patientContext?.available_actions?.map((action) => {
                const cmdInfo = allCommands.find(c => c.id === action.id);
                const Icon = getIcon(cmdInfo?.icon || "file-text");
                return (
                  <Button
                    key={action.id}
                    variant="ghost"
                    className={`w-full justify-start ${
                      isDark 
                        ? 'text-slate-300 hover:text-white hover:bg-slate-800' 
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    } ${action.priority === "high" ? "border-l-2 border-teal-500" : ""}`}
                    onClick={() => openOverlay(action.id)}
                    data-testid={`quick-action-${action.id}`}
                  >
                    <Icon className={`w-4 h-4 mr-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                    {action.name}
                  </Button>
                );
              })}
            </div>
          </div>
          
          {/* Patient Stats */}
          {patientContext?.quick_stats && (
            <div>
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${
                isDark ? 'text-slate-500' : 'text-gray-500'
              }`}>Patient Info</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Benefit Period</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{patientContext.quick_stats.benefit_period}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Days in Program</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{patientContext.quick_stats.days_in_program}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Level of Care</span>
                  <Badge className={isDark ? 'bg-teal-500/20 text-teal-400' : 'bg-teal-100 text-teal-700'}>{patientContext.quick_stats.loc}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Last Visit</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{patientContext.quick_stats.last_visit}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Next Visit</span>
                  <span className="text-teal-600">{patientContext.quick_stats.next_visit}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Keyboard Shortcuts */}
          <div className={`mt-8 pt-6 border-t ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2 ${
              isDark ? 'text-slate-500' : 'text-gray-500'
            }`}>
              <Keyboard className="w-3 h-3" />
              Shortcuts
            </h3>
            <div className="space-y-2 text-xs">
              <div className={`flex justify-between ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                <span>Command Palette</span>
                <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-800' : 'bg-gray-200'}`}>⌘K</kbd>
              </div>
              <div className={`flex justify-between ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                <span>New Note</span>
                <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-800' : 'bg-gray-200'}`}>⌘N</kbd>
              </div>
              <div className={`flex justify-between ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                <span>Medications</span>
                <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-800' : 'bg-gray-200'}`}>⌘M</kbd>
              </div>
              <div className={`flex justify-between ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                <span>Patient Journey</span>
                <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-800' : 'bg-gray-200'}`}>⌘J</kbd>
              </div>
            </div>
          </div>
        </div>

        {/* Main Timeline */}
        <div className="flex-1 p-6">
          <div className="max-w-5xl mx-auto">
            {/* Census Eligibility Dashboard Widget - Role-based view */}
            <div className="mb-6">
              <CensusEligibilityWidget 
                isDark={isDark} 
                userRole={user?.role || "administrator"}
                currentPatientId={selectedPatient?.id || patientContext?.patient?.id}
                currentPatientName={selectedPatient?.name || patientContext?.patient?.name}
              />
            </div>
            
            {/* Timeline Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className={`text-xl font-semibold ${isDark ? '' : 'text-gray-900'}`}>Patient Timeline</h2>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>All clinical events in one stream</p>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                  <Input
                    placeholder="Search timeline..."
                    value={timelineFilters.search_text}
                    onChange={(e) => setTimelineFilters({ ...timelineFilters, search_text: e.target.value })}
                    className={`pl-9 w-full sm:w-64 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-300'}`}
                    data-testid="timeline-search"
                  />
                </div>
                
                {/* Filter */}
                <Button
                  variant="outline"
                  className={isDark ? 'border-slate-700 bg-slate-800' : 'border-gray-300 bg-white'}
                  onClick={() => setShowFilters(!showFilters)}
                  data-testid="timeline-filter-btn"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
            
            {/* Filter Panel */}
            {showFilters && (
              <Card className={`mb-6 ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(EVENT_CONFIG).map(([type, config]) => (
                      <Badge
                        key={type}
                        variant="outline"
                        className={`cursor-pointer transition-colors ${
                          !timelineFilters.event_types || timelineFilters.event_types.includes(type)
                            ? `${config.color} text-white border-transparent`
                            : isDark ? "bg-slate-800 text-slate-400 border-slate-600" : "bg-gray-100 text-gray-600 border-gray-300"
                        }`}
                        onClick={() => {
                          if (!timelineFilters.event_types) {
                            setTimelineFilters({ ...timelineFilters, event_types: [type] });
                          } else if (timelineFilters.event_types.includes(type)) {
                            const newTypes = timelineFilters.event_types.filter(t => t !== type);
                            setTimelineFilters({ ...timelineFilters, event_types: newTypes.length ? newTypes : null });
                          } else {
                            setTimelineFilters({ ...timelineFilters, event_types: [...timelineFilters.event_types, type] });
                          }
                        }}
                      >
                        {config.label}
                      </Badge>
                    ))}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setTimelineFilters({ event_types: null, search_text: "" })}
                    >
                      Clear All
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Timeline Events */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
              </div>
            ) : (
              <div className="relative">
                {/* Timeline Line */}
                <div className={`absolute left-6 top-0 bottom-0 w-0.5 ${isDark ? 'bg-slate-800' : 'bg-gray-200'}`} />
                
                {/* Events */}
                <div className="space-y-4">
                  {timeline.map((event) => {
                    const config = EVENT_CONFIG[event.type] || { color: "bg-slate-500", label: "Event" };
                    const Icon = ICONS[config.icon] || FileText;
                    
                    return (
                      <div 
                        key={event.id} 
                        className="relative pl-14 group"
                        data-testid={`timeline-event-${event.id}`}
                      >
                        {/* Event Marker */}
                        <div className={`absolute left-4 w-5 h-5 rounded-full ${config.color} flex items-center justify-center ring-4 ${isDark ? 'ring-slate-950' : 'ring-gray-50'}`}>
                          <FileText className="w-3 h-3 text-white" />
                        </div>
                        
                        {/* Event Card */}
                        <Card className={`transition-colors cursor-pointer ${
                          isDark 
                            ? 'bg-slate-800/50 border-slate-700 hover:bg-slate-800' 
                            : 'bg-white border-gray-200 hover:bg-gray-50 shadow-sm'
                        }`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className={`text-xs ${isDark ? 'border-slate-600' : 'border-gray-300'}`}>
                                    {config.label}
                                  </Badge>
                                  <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{event.discipline}</span>
                                </div>
                                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{event.title}</h4>
                                <p className={`text-sm mt-1 line-clamp-2 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{event.summary}</p>
                                <div className={`flex items-center gap-4 mt-2 text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {event.staff_name}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {new Date(event.timestamp).toLocaleString()}
                                  </span>
                                </div>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => openOverlay("event_detail", event)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Command Palette */}
      <CommandPalette
        isOpen={commandOpen}
        onClose={() => setCommandOpen(false)}
        commands={allCommands}
        onExecuteCommand={executeCommand}
        contextualSuggestions={contextualSuggestions}
        patientName={selectedPatient?.name}
      />

      {/* Dynamic Overlay Renderer */}
      <OverlayRenderer
        activeOverlay={activeOverlay}
        overlayData={overlayData}
        patientId={selectedPatient?.id}
        patientName={selectedPatient?.name}
        userRole={user?.role || user?.user_type}
        onClose={closeOverlay}
        onSave={() => {
          toast.success("Saved successfully");
          closeOverlay();
        }}
      />

      {/* Clinical Overlays - Special overlays not in OverlayRenderer */}
      <Sheet open={activeOverlay === "visit_note" || activeOverlay === "new_visit_note"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className="w-[1200px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              New Visit Note
              <Badge className="ml-2 bg-violet-500/20 text-violet-400 border-0">
                <Sparkles className="w-3 h-3 mr-1" />
                AI Assisted
              </Badge>
            </SheetTitle>
            <SheetDescription className="text-slate-400">
              Document your clinical visit with AI-powered suggestions.
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-4">
            <SmartDocumentation
              patientId={selectedPatient?.id || "demo-patient"}
              patientName={selectedPatient?.name || "Demo Patient"}
              patientDiagnosis={selectedPatient?.diagnosis || selectedPatient?.primary_diagnosis}
              noteType="skilled_nurse"
              initialText={noteContent}
              onTextChange={(text) => checkCompliance(text)}
              onFunctionalAssessmentComplete={(assessment) => {
                console.log("Functional assessment completed:", assessment);
              }}
              mode="full"
              showFunctionalAssessment={true}
            />
            
            {complianceResult && (
              <div className={`mt-4 p-3 rounded-lg border ${
                complianceResult.grade === "A" ? "bg-green-500/10 border-green-500/30" :
                complianceResult.grade === "B" ? "bg-blue-500/10 border-blue-500/30" :
                "bg-amber-500/10 border-amber-500/30"
              }`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Documentation Grade</span>
                  <Badge className={
                    complianceResult.grade === "A" ? "bg-green-500" :
                    complianceResult.grade === "B" ? "bg-blue-500" :
                    "bg-amber-500"
                  }>
                    Grade {complianceResult.grade}
                  </Badge>
                </div>
                {complianceResult.missing_fields?.length > 0 && (
                  <div className="mt-2 text-sm text-amber-400">
                    <p>Missing: {complianceResult.missing_fields.join(", ")}</p>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-slate-800">
              <Button variant="outline" onClick={closeOverlay}>Cancel</Button>
              <Button className="bg-teal-600 hover:bg-teal-700">Save Note</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Smart Documentation Overlay */}
      <Sheet open={activeOverlay === "smart_documentation"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className="w-[1200px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Brain className="w-5 h-5 text-violet-400" />
              Smart Documentation
              <Badge className="ml-2 bg-violet-500/20 text-violet-400 border-0">
                <Sparkles className="w-3 h-3 mr-1" />
                GPT-5.2 Powered
              </Badge>
            </SheetTitle>
            <SheetDescription className="text-slate-400">
              AI-powered documentation assistant with real-time suggestions.
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-4">
            <SmartDocumentation
              patientId={selectedPatient?.id || "demo-patient"}
              noteType="skilled_nurse"
              mode="full"
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* IDG Review Mode */}
      <Sheet open={activeOverlay === "idg_review" || activeOverlay === "idg_dictation"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className="w-[800px] max-w-[95vw] bg-slate-900 border-slate-700 text-white">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-400" />
              IDG Review Mode
            </SheetTitle>
            <SheetDescription className="text-slate-400">
              Team review with voice transcription
            </SheetDescription>
          </SheetHeader>
          
          <IDGVoiceDictation 
            patientId={selectedPatient?.id}
            onClose={closeOverlay}
          />
        </SheetContent>
      </Sheet>

      {/* Timeline Event Detail */}
      <Sheet open={activeOverlay === "event_detail"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className="w-[700px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              {overlayData?.type && EVENT_CONFIG[overlayData.type] ? (
                <div className={`w-6 h-6 rounded-full ${EVENT_CONFIG[overlayData.type].color} flex items-center justify-center`}>
                  <FileText className="w-3 h-3 text-white" />
                </div>
              ) : (
                <FileText className="w-5 h-5 text-slate-400" />
              )}
              {overlayData?.title || "Event Details"}
            </SheetTitle>
            <SheetDescription className="text-slate-400">
              {overlayData?.type ? EVENT_CONFIG[overlayData.type]?.label : "Timeline Event"} • {overlayData?.discipline}
            </SheetDescription>
          </SheetHeader>
          
          {overlayData && (
            <div className="mt-6 space-y-6">
              {/* Event Summary */}
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <p className="text-slate-300">{overlayData.summary}</p>
                </CardContent>
              </Card>

              {/* Event Metadata */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500">Staff</span>
                    </div>
                    <p className="text-sm text-white">{overlayData.staff_name}</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500">Date/Time</span>
                    </div>
                    <p className="text-sm text-white">
                      {overlayData.timestamp ? new Date(overlayData.timestamp).toLocaleString() : "N/A"}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Time In/Out if available */}
              {(overlayData.time_in || overlayData.time_out) && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-400" />
                      Visit Time
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="flex items-center gap-6">
                      <div>
                        <span className="text-xs text-slate-500">Time In</span>
                        <p className="text-white font-medium">{overlayData.time_in || "—"}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-500" />
                      <div>
                        <span className="text-xs text-slate-500">Time Out</span>
                        <p className="text-white font-medium">{overlayData.time_out || "—"}</p>
                      </div>
                      {overlayData.duration_minutes && (
                        <div className="ml-auto">
                          <span className="text-xs text-slate-500">Duration</span>
                          <p className="text-emerald-400 font-medium">
                            {Math.floor(overlayData.duration_minutes / 60) > 0 
                              ? `${Math.floor(overlayData.duration_minutes / 60)}h ${overlayData.duration_minutes % 60}m`
                              : `${overlayData.duration_minutes}m`
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Full Content if available */}
              {overlayData.details?.content && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-400" />
                      Full Documentation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="bg-slate-900 p-4 rounded-lg">
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">
                        {overlayData.details.content}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Additional Details */}
              {overlayData.details && Object.keys(overlayData.details).filter(k => k !== 'content' && k !== 'functional_assessment').length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Additional Details</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="space-y-2">
                      {Object.entries(overlayData.details)
                        .filter(([key]) => key !== 'content' && key !== 'functional_assessment')
                        .map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-slate-400 text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="text-white text-sm">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </span>
                          </div>
                        ))
                      }
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Functional Assessment if available */}
              {overlayData.details?.functional_assessment && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Activity className="w-4 h-4 text-emerald-400" />
                      Functional Assessment
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="grid grid-cols-2 gap-4">
                      {overlayData.details.functional_assessment.pps !== undefined && (
                        <div className="text-center p-3 bg-slate-900 rounded-lg">
                          <p className="text-xs text-slate-500">PPS Score</p>
                          <p className="text-2xl font-bold text-emerald-400">{overlayData.details.functional_assessment.pps}%</p>
                        </div>
                      )}
                      {overlayData.details.functional_assessment.kps !== undefined && (
                        <div className="text-center p-3 bg-slate-900 rounded-lg">
                          <p className="text-xs text-slate-500">KPS Score</p>
                          <p className="text-2xl font-bold text-blue-400">{overlayData.details.functional_assessment.kps}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Clinical Logic Layer */}
      <Sheet open={activeOverlay === "clinical_logic"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className="w-[900px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Brain className="w-5 h-5 text-violet-400" />
              Clinical Logic Layer
            </SheetTitle>
            <SheetDescription className="text-slate-400">
              14-Day lookback analysis and POC change proposals
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-4">
            <ClinicalLogicDashboard patientId={selectedPatient?.id} />
          </div>
        </SheetContent>
      </Sheet>

      {/* HOPE Assessment */}
      <Sheet open={activeOverlay === "hope_assessment"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className="w-[800px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white">HOPE Assessment</SheetTitle>
            <SheetDescription className="text-slate-400">
              Complete or review HOPE assessment forms
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-4">
            <HOPEForm patientId={selectedPatient?.id} />
          </div>
        </SheetContent>
      </Sheet>

      {/* AI Chat */}
      <Sheet open={activeOverlay === "ai_chat"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className="w-[600px] max-w-[95vw] bg-slate-900 border-slate-700 text-white">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-400" />
              AI Clinical Assistant
            </SheetTitle>
            <SheetDescription className="text-slate-400">
              Get clinical guidance and support
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-4 h-[calc(100vh-200px)]">
            <ProactiveAIChat patientId={selectedPatient?.id} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Visit Template Builder */}
      <Sheet open={activeOverlay === "visit_template_builder"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className="w-[900px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white">Visit Template Builder</SheetTitle>
            <SheetDescription className="text-slate-400">
              CMS CoP-compliant discipline-specific visit templates
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-4">
            <HospiceVisitTemplateBuilder />
          </div>
        </SheetContent>
      </Sheet>

      {/* Compliance Dashboard */}
      <Sheet open={activeOverlay === "compliance_dashboard"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className="w-[900px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-400" />
              Compliance Dashboard
            </SheetTitle>
            <SheetDescription className="text-slate-400">
              Supervision tracking, QAPI reporting, and compliance guardrails
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-4">
            <ComplianceDashboard />
          </div>
        </SheetContent>
      </Sheet>

      {/* Voice Settings */}
      <Sheet open={activeOverlay === "voice_settings"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className="w-[700px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white">Voice Command Settings</SheetTitle>
            <SheetDescription className="text-slate-400">
              Customize voice shortcuts and preferences
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            <VoiceCommandSettings 
              userId={selectedPatient?.id || "demo-user"} 
              onClose={closeOverlay} 
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Patient Journey Dashboard */}
      <Sheet open={activeOverlay === "patient_journey"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className="w-[900px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-teal-400" />
              Patient Journey Dashboard
            </SheetTitle>
            <SheetDescription className="text-slate-400">
              Track patient complete hospice journey from intake to discharge
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            <PatientJourneyDashboard 
              patientId={selectedPatient?.id}
              patientName={selectedPatient?.name}
              onClose={closeOverlay}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Clinical AI Agent Panel */}
      <ClinicalAIAgentPanel
        isOpen={activeOverlay === "clinical_ai_agent"}
        onClose={closeOverlay}
        patientId={selectedPatient?.id}
        patientName={selectedPatient?.name}
        onAssessmentComplete={(type, data) => {
          toast.success(`${type} assessment completed`);
          fetchPatientContext(selectedPatient?.id);
        }}
      />

      {/* Export Report Overlay */}
      <ExportReportOverlay
        open={activeOverlay === "export_report" || activeOverlay === "export"}
        onOpenChange={closeOverlay}
        patient={selectedPatient ? { name: selectedPatient.name, id: selectedPatient.id } : null}
        chartId={selectedPatient?.id}
      />

      {/* Unified Visit Note Overlay */}
      <UnifiedVisitNote
        visitId={overlayData?.visit_id || overlayData?.visitId}
        patientId={selectedPatient?.id}
        patientName={selectedPatient?.name}
        isOpen={activeOverlay === "unified_visit_note" || activeOverlay === "unified_visit"}
        onClose={closeOverlay}
        onVisitUpdated={() => {
          toast.success("Visit updated");
          fetchTimeline(selectedPatient?.id);
        }}
        isDark={isDark}
      />

      {/* Visit Sync Admin Panel */}
      <Sheet open={activeOverlay === "visit_sync_admin"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className={`w-[900px] max-w-[95vw] overflow-y-auto ${
          isDark ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-200'
        }`}>
          <SheetHeader>
            <SheetTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Shield className="w-5 h-5 text-teal-500" />
              Visit Sync & Compliance Admin
            </SheetTitle>
            <SheetDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
              Configure CMS hospice compliance rules and visit synchronization settings
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            <VisitSyncAdmin isDark={isDark} />
          </div>
        </SheetContent>
      </Sheet>

      {/* LCD Eligibility Overlay */}
      <LCDEligibilityOverlay
        isOpen={activeOverlay === "lcd_eligibility"}
        onClose={closeOverlay}
        patientId={selectedPatient?.id}
        patientName={selectedPatient?.name}
      />

      {/* AI Care Plan Overlay */}
      <AICarePlanOverlay
        isOpen={activeOverlay === "ai_care_plan"}
        onClose={closeOverlay}
        patientId={selectedPatient?.id}
        patientName={selectedPatient?.name}
        patientContext={patientContext}
      />

      {/* RCM Management Overlay (Admin Only) */}
      <RCMManagementOverlay
        isOpen={activeOverlay === "rcm_management"}
        onClose={closeOverlay}
        userRole={user?.role || user?.user_type}
      />

      {/* Staff Scheduling Overlay (Admin Only) */}
      <StaffSchedulingOverlay
        isOpen={activeOverlay === "staff_scheduling"}
        onClose={closeOverlay}
        userRole={user?.role || user?.user_type}
      />

      {/* Acuity Trends Overlay (Admin Only) */}
      <AcuityTrendsOverlay
        isOpen={activeOverlay === "acuity_trends"}
        onClose={closeOverlay}
        userRole={user?.role || user?.user_type}
      />

      {/* Fall Event Documentation Overlay */}
      <Sheet open={activeOverlay === "fall_event" || activeOverlay === "fall_documentation"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className="w-[800px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-y-auto">
          <FallEventOverlay
            patientId={selectedPatient?.id}
            patientName={selectedPatient?.name}
            onSave={() => {
              toast.success("Fall event documented");
              closeOverlay();
            }}
            onClose={closeOverlay}
          />
        </SheetContent>
      </Sheet>

      {/* Fall Risk Dashboard Overlay (Admin Only) */}
      <FallRiskDashboardOverlay
        isOpen={activeOverlay === "fall_risk_dashboard"}
        onClose={closeOverlay}
        userRole={user?.role || user?.user_type}
      />

      {/* Clinical Assessments Panel Overlay */}
      <Sheet open={activeOverlay === "clinical_assessments"} onOpenChange={closeOverlay}>
        <SheetContent side="right" className="w-[600px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-y-auto">
          <ClinicalAssessmentsPanel
            patientId={selectedPatient?.id}
            patientName={selectedPatient?.name}
            onOpenOverlay={openOverlay}
            isDark={isDark}
          />
        </SheetContent>
      </Sheet>

      {/* IDG Voice Meeting Overlay */}
      <IDGVoiceMeetingOverlay
        isOpen={activeOverlay === "idg_voice_meeting"}
        onClose={closeOverlay}
      />

      {/* IDG Meeting Analytics Overlay */}
      <IDGMeetingAnalyticsOverlay
        isOpen={activeOverlay === "idg_meeting_analytics"}
        onClose={closeOverlay}
      />

      {/* IDG Insights Email Settings Overlay */}
      <IDGInsightsEmailOverlay
        isOpen={activeOverlay === "idg_insights_email"}
        onClose={closeOverlay}
      />

      {/* Voice Commands Help Overlay */}
      <VoiceHelpOverlay
        isOpen={activeOverlay === "voice_help"}
        onClose={closeOverlay}
      />
    </div>
  );
}
